'use client';

import {
  createContext,
  forwardRef,
  useContext,
  type ForwardedRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Box, css, cx } from '@terra/ui-styled-system';
import { LabelDefault, type LabelDefaultProps } from './typography';
import * as StyledField from './styled/field';
import {
  FieldCopilotNote,
  type FieldCopilotNoteProps,
} from './field-copilot-note';

type FieldLayoutContextValue = {
  reviewing: boolean;
  borderless: boolean;
};

const FieldLayoutContext = createContext<FieldLayoutContextValue>({
  reviewing: false,
  borderless: false,
});

const useFieldLayoutContext = () => useContext(FieldLayoutContext);

type FieldRootLayoutProps = Pick<
  StyledField.RootProps,
  | 'invalid'
  | 'disabled'
  | 'required'
  | 'id'
  | 'className'
  | 'w'
  | 'width'
  | 'maxW'
  | 'minW'
  | 'flex'
  | 'flexGrow'
  | 'alignSelf'
  | 'gap'
  | 'mb'
  | 'gridColumn'
>;

export type FieldRootProps = FieldRootLayoutProps & {
  reviewing?: boolean;
  borderless?: boolean;
  children?: ReactNode;
  'data-field-anchor'?: string;
  'data-pending-review-field'?: string;
};

export const Root = forwardRef<HTMLDivElement, FieldRootProps>(
  function FieldRoot(props, ref) {
    const {
      reviewing,
      borderless,
      children,
      invalid,
      disabled,
      required,
      id,
      className,
      w,
      width,
      maxW,
      minW,
      flex,
      flexGrow,
      alignSelf,
      gap,
      mb,
      gridColumn,
      'data-field-anchor': dataFieldAnchor,
      'data-pending-review-field': dataPendingReviewField,
    } = props;

    return (
      <FieldLayoutContext.Provider
        value={{
          reviewing: !!reviewing,
          borderless: !!borderless,
        }}
      >
        <StyledField.Root
          ref={ref}
          invalid={invalid}
          disabled={disabled}
          required={required}
          id={id}
          className={className}
          w={w}
          width={width}
          maxW={maxW}
          minW={minW}
          flex={flex}
          flexGrow={flexGrow}
          alignSelf={alignSelf}
          gap={gap}
          mb={mb}
          gridColumn={gridColumn}
          data-field-anchor={dataFieldAnchor}
          data-pending-review-field={dataPendingReviewField}
        >
          {children}
        </StyledField.Root>
      </FieldLayoutContext.Provider>
    );
  },
);

Root.displayName = 'Field.Root';

export const RootProvider = StyledField.RootProvider;
export const Label = StyledField.Label;
export const HelperText = StyledField.HelperText;
export const ErrorText = StyledField.ErrorText;
export const Select = StyledField.Select;
export const Input = StyledField.Input;
export const Textarea = StyledField.Textarea;
export const Context = StyledField.Context;

export type FieldMandatoryProps = {
  children?: ReactNode;
  color?: LabelDefaultProps['color'];
  as?: LabelDefaultProps['as'];
};

export const Mandatory = forwardRef(function FieldMandatory(
  props: FieldMandatoryProps,
  ref: ForwardedRef<HTMLLabelElement>,
) {
  const { children = '*', color = 'fg.error', as = 'span' } = props;

  return (
    <LabelDefault ref={ref} as={as} color={color}>
      {children}
    </LabelDefault>
  );
});

Mandatory.displayName = 'Field.Mandatory';

const reviewingControlClass = css({
  '& > *': {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.accent.default',
  },
});

const ReviewingChrome = forwardRef<
  HTMLDivElement,
  {
    applyReviewing: boolean;
    className?: string;
    children?: ReactNode;
  }
>(function ReviewingChrome({ applyReviewing, className, children }, ref) {
  return (
    <Box
      ref={ref}
      w="full"
      data-reviewing={applyReviewing ? '' : undefined}
      className={cx(
        applyReviewing ? reviewingControlClass : undefined,
        className,
      )}
    >
      {children}
    </Box>
  );
});

export type FieldControlProps = {
  borderless?: boolean;
  children?: ReactNode;
  className?: string;
};

export const Control = forwardRef<HTMLDivElement, FieldControlProps>(
  function FieldControl(props, ref) {
    const { borderless: borderlessProp, children, className } = props;
    const { reviewing, borderless: contextBorderless } =
      useFieldLayoutContext();
    const borderless = borderlessProp ?? contextBorderless;
    const applyReviewing = reviewing && !borderless;

    return (
      <ReviewingChrome
        ref={ref}
        applyReviewing={applyReviewing}
        className={className}
      >
        {children}
      </ReviewingChrome>
    );
  },
);

Control.displayName = 'Field.Control';

export type FieldReviewingRingProps = {
  active?: boolean;
  borderless?: boolean;
  children: ReactElement;
  className?: string;
};

export const ReviewingRing = ({
  active,
  borderless,
  children,
  className,
}: FieldReviewingRingProps) => {
  const { reviewing, borderless: contextBorderless } = useFieldLayoutContext();
  const isActive = active ?? reviewing;
  const isBorderless = borderless ?? contextBorderless;
  const applyReviewing = !!isActive && !isBorderless;

  return (
    <ReviewingChrome applyReviewing={applyReviewing} className={className}>
      {children}
    </ReviewingChrome>
  );
};

ReviewingRing.displayName = 'Field.ReviewingRing';

export type { FieldCopilotNoteProps };

export const CopilotNote = FieldCopilotNote;
